import React, { useState, useEffect, useMemo } from 'react';
import Select from 'react-select';
import { useTranslation } from 'react-i18next';
import { API_ENDPOINTS } from '../../config/properties';
import Modal from 'react-modal';
import { useAuth } from '../../context/AuthContext';
import httpClient from '../../services/httpClient';
import './Championships.css';

const Championships = () => {
  const { t } = useTranslation();
  const { isAuthenticated, loading: authLoading } = useAuth();
  const [years, setYears] = useState([]);
  const [leagues, setLeagues] = useState([]);
  const [teams, setTeams] = useState([]);
  const [matchStats, setMatchStats] = useState([]);
  const [selectedYear, setSelectedYear] = useState(null);
  const [selectedLeague, setSelectedLeague] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [modalContent, setModalContent] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (authLoading) {
      return;
    }

    if (!isAuthenticated) {
      setError('Authentication required');
      return;
    }

    const fetchYears = async () => {
      try {
        setError(null);
        const response = await httpClient.get(API_ENDPOINTS.years);
        const data = await response.json();
        setYears(data);
        if (data.length > 0) {
          setSelectedYear(data[data.length - 1]);
        }
      } catch (err) {
        console.error('Error loading years:', err);
        setError(t('errorLoadingYears') || 'Error loading years');
      }
    };

    fetchYears();
  }, [authLoading, isAuthenticated, t]);

  useEffect(() => {
    if (authLoading) {
      return;
    }

    if (!isAuthenticated) {
      setError('Authentication required');
      return;
    }

    const fetchLeagues = async () => {
      try {
        setError(null);
        const response = await httpClient.get(API_ENDPOINTS.leagues);
        const data = await response.json();
        setLeagues(data);
        if (data.length > 0) {
          setSelectedLeague('I1');
        }
      } catch (err) {
        console.error('Error loading leagues:', err);
        setError(t('errorLoadingLeagues') || 'Error loading leagues');
      }
    };

    fetchLeagues();
  }, [authLoading, isAuthenticated, t]);

  useEffect(() => {
    if (authLoading || !isAuthenticated) {
      return;
    }

    if (selectedYear && selectedLeague) {
      const fetchTeams = async () => {
        try {
          setError(null);
          const response = await httpClient.get(`${API_ENDPOINTS.teams}?year=${selectedYear}&lagueCode=${selectedLeague}`);
          const data = await response.json();
          setTeams(data);
        } catch (err) {
          console.error('Error loading teams:', err);
          setError(t('errorLoadingTeams') || 'Error loading teams');
        }
      };

      fetchTeams();
    }
  }, [selectedYear, selectedLeague, authLoading, isAuthenticated, t]);

  useEffect(() => {
    if (authLoading) {
      return;
    }

    if (!isAuthenticated) {
      return;
    }

    const fetchMatchStats = async () => {
      try {
        setError(null);
        const response = await httpClient.get(API_ENDPOINTS.matchStats);
        const data = await response.json();
        setMatchStats(data);
      } catch (err) {
        console.error('Error loading match stats:', err);
        setError(t('errorLoadingMatchStats') || 'Error loading match stats');
      }
    };

    fetchMatchStats();
  }, [authLoading, isAuthenticated, t]);

  const handleYearChange = (selectedOption) => {
    setSelectedYear(selectedOption.value);
  };

  const handleLeagueChange = (selectedOption) => {
    setSelectedLeague(selectedOption.value);
  };

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
  };

  const openModal = (match) => {
    setModalContent(match);
    setModalIsOpen(true);
  };

  const closeModal = () => {
    setModalIsOpen(false);
    setModalContent(null);
  };

  const calculateStats = (teamName, matches) => {
    let points = 0;
    let goalsFor = 0;
    let goalsAgainst = 0;
    let wins = 0;
    let draws = 0;
    let losses = 0;
    let matchesPlayed = 0;
    let goalDifference = 0;

    matches.forEach((match) => {
      matchesPlayed++;
      if (match.homeTeam === teamName) {
        goalsFor += match.fthg;
        goalsAgainst += match.ftag;
        if (match.ftr === 'H') {
          points += 3;
          wins += 1;
        } else if (match.ftr === 'D') {
          points += 1;
          draws += 1;
        } else {
          losses += 1;
        }
      } else if (match.awayTeam === teamName) {
        goalsFor += match.ftag;
        goalsAgainst += match.fthg;
        if (match.ftr === 'A') {
          points += 3;
          wins += 1;
        } else if (match.ftr === 'D') {
          points += 1;
          draws += 1;
        } else {
          losses += 1;
        }
      }
    });

    goalDifference = goalsFor - goalsAgainst;

    return { points, goalsFor, goalsAgainst, wins, draws, losses, matchesPlayed, goalDifference };
  };

  const sortedTeams = useMemo(() => {
    const statsCache = new Map();

    teams.forEach((team) => {
      const teamMatches = matchStats.filter(
        (match) =>
          match.year === selectedYear &&
          (match.homeTeam === team.name || match.awayTeam === team.name)
      );

      const { points, goalsFor, goalsAgainst, wins, draws, losses, matchesPlayed, goalDifference } = calculateStats(team.name, teamMatches);
      statsCache.set(team.name, { points, goalsFor, goalsAgainst, wins, draws, losses, matchesPlayed, goalDifference });
    });

    return [...teams]
      .map((team) => {
        const stats = statsCache.get(team.name) || { points: 0, goalsFor: 0, goalsAgainst: 0, wins: 0, draws: 0, losses: 0, matchesPlayed: 0, goalDifference: 0 };
        return { ...team, ...stats };
      })
      .sort((a, b) => b.points - a.points);
  }, [teams, matchStats, selectedYear]);

  const resultDot = (teamName, match) => {
    const { ftr, date, homeTeam, awayTeam, fthg, ftag } = match;

    let color = '';
    let result = '';
    let ariaLabel = `${t('date')}: ${new Date(date).toLocaleDateString()}, ${t('teams')}: ${homeTeam} vs ${awayTeam}, ${t('score')}: ${fthg}-${ftag}`;

    if (homeTeam === teamName) {
      if (ftr === 'H') {
        color = 'green';
        result = 'Win';
      }
      else if (ftr === 'A') {
        color = 'red';
        result = 'Loss';
      }
      else if (ftr === 'D') {
        color = 'yellow';
        result = 'Draw';
      }
    } else if (awayTeam === teamName) {
      if (ftr === 'A') {
        color = 'green';
        result = 'Win';
      }
      else if (ftr === 'H') {
        color = 'red';
        result = 'Loss';
      }
      else if (ftr === 'D') {
        color = 'yellow';
        result = 'Draw';
      }
    }

    return (
      <button
        className={`dot ${color}`}
        title={ariaLabel}
        onClick={() => openModal(match)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            openModal(match);
          }
        }}
        aria-label={`${result}: ${ariaLabel}. Click for details.`}
        tabIndex="0"
      />
    );
  };

  // Show loading state while auth is initializing
  if (authLoading) {
    return (
      <div className="container mt-5">
        <div className="alert alert-info">
          {t('loading') || 'Loading...'}
        </div>
      </div>
    );
  }

  // Show error if not authenticated
  if (!isAuthenticated) {
    return (
      <div className="container mt-5">
        <div className="alert alert-warning">
          {t('authenticationRequired') || 'Authentication required. Please log in.'}
        </div>
      </div>
    );
  }

  return (
    <div className="container mt-5">
      <div className="championships-header mb-4">
        <h1>{t('titlePageTabChampionships')}</h1>
        <p className="mb-0 mt-2" style={{ fontSize: '0.95rem', opacity: 0.95 }}>
          {t('viewFootballStatistics') || 'View championship standings and team performance'}
        </p>
      </div>

      {error && (
        <div className="alert alert-danger mb-3">
          {error}
        </div>
      )}

      <div className="card shadow-lg">
        <div className="card-body p-4">
          <div className="championship-filters mb-4">
            <div className="row g-3">
              <div className="col-md-4">
                <label htmlFor="year" className="form-label fw-600">{t('year')}</label>
                <Select
                  id="year"
                  options={years.map((year) => ({ label: year.toString(), value: year }))}
                  onChange={handleYearChange}
                  placeholder={t('selectYear')}
                  value={
                    years.includes(selectedYear)
                      ? { label: selectedYear.toString(), value: selectedYear }
                      : null
                  }
                  className="react-select-container"
                />
              </div>
              <div className="col-md-4">
                <label htmlFor="lagueCode" className="form-label fw-600">{t('league')}</label>
                <Select
                  id="lagueCode"
                  options={leagues.map((league) => ({
                    label: league.name,
                    value: league.code,
                  }))}
                  onChange={handleLeagueChange}
                  placeholder={t('selectLeague')}
                  value={
                    leagues.find((league) => league.code === selectedLeague)
                      ? { label: leagues.find((league) => league.code === selectedLeague).name, value: selectedLeague }
                      : null
                  }
                  className="react-select-container"
                />
              </div>
              <div className="col-md-4">
                <label htmlFor="search" className="form-label fw-600">{t('searchTeam')}</label>
                <div className="position-relative">
                  <input
                    id="search"
                    placeholder={t('searchTeam')}
                    className="form-control"
                    type="text"
                    value={searchTerm}
                    onChange={handleSearchChange}
                    style={{ paddingLeft: '2.5rem' }}
                  />
                </div>
              </div>
            </div>
          </div>

          {selectedYear && selectedLeague && (
            <div className="alert alert-info mb-4" style={{ borderLeft: '4px solid #667eea' }}>
              <strong>{t('league')}:</strong> {leagues.find((league) => league.code === selectedLeague)?.name || selectedLeague}
              <br />
              <strong>{t('year')}:</strong> {selectedYear}
            </div>
          )}

          <div className="table-responsive">
            <table className="table table-hover" role="table" aria-label={`Championship standings for ${leagues.find((league) => league.code === selectedLeague)?.name || 'selected league'} in ${selectedYear}`}>
              <thead>
                <tr>
                  <th scope="col" style={{ minWidth: '150px' }}>{t('team')}</th>
                  <th scope="col" className="text-center">{t('points')}</th>
                  <th scope="col" className="text-center" title={t('goalsFor') || 'Goals scored'}>{t('goalsFor')}</th>
                  <th scope="col" className="text-center" title={t('goalsAgainst') || 'Goals conceded'}>{t('goalsAgainst')}</th>
                  <th scope="col" className="text-center">{t('goalDifference')}</th>
                  <th scope="col" className="text-center">{t('matchesPlayed')}</th>
                  <th scope="col" className="text-center">{t('wins')}</th>
                  <th scope="col" className="text-center">{t('draws')}</th>
                  <th scope="col" className="text-center">{t('losses')}</th>
                  <th scope="col" style={{ minWidth: '200px' }}>{t('matchResults')}</th>
                </tr>
              </thead>
              <tbody>
                {sortedTeams.map((team, index) => {
                  const teamMatches = matchStats.filter(
                    (match) =>
                      match.year === selectedYear &&
                      (match.homeTeam === team.name || match.awayTeam === team.name)
                  );

                  return (
                    <tr key={team.code}>
                      <td className="team-name">
                        <span style={{ marginRight: '0.5rem', fontWeight: 'bold', color: '#667eea' }}>#{index + 1}</span>
                        {team.name}
                      </td>
                      <td className="text-center">
                        <span className="points-badge">{team.points}</span>
                      </td>
                      <td className="text-center">
                        <span className="stat-badge goals">{team.goalsFor}</span>
                      </td>
                      <td className="text-center">
                        <span className="stat-badge goals">{team.goalsAgainst}</span>
                      </td>
                      <td className="text-center">
                        <span style={{ fontWeight: '600', color: team.goalDifference > 0 ? '#11998e' : team.goalDifference < 0 ? '#f5576c' : '#999' }}>
                          {team.goalDifference > 0 ? '+' : ''}{team.goalDifference}
                        </span>
                      </td>
                      <td className="text-center">{team.matchesPlayed}</td>
                      <td className="text-center">
                        <span className="stat-badge wins">{team.wins}</span>
                      </td>
                      <td className="text-center">
                        <span className="stat-badge draws">{team.draws}</span>
                      </td>
                      <td className="text-center">
                        <span className="stat-badge losses">{team.losses}</span>
                      </td>
                      <td>
                        {teamMatches.length === 0 ? (
                          <span style={{ color: '#999', fontSize: '0.9rem' }}>{t('noMatches')}</span>
                        ) : (
                          <div className="match-results">
                            {teamMatches.map((match) => (
                              <span key={match.id} className="match-dot">
                                {resultDot(team.name, match)}
                              </span>
                            ))}
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <Modal
        isOpen={modalIsOpen}
        onRequestClose={closeModal}
        contentLabel="Match Details Dialog"
        className="custom-modal"
        overlayClassName="custom-modal-overlay"
        appElement={document.getElementById('root')}
        role="dialog"
        aria-modal="true"
        aria-labelledby="modal-title"
      >
        {modalContent && (
          <div className="modal-content">
            <button
              onClick={closeModal}
              className="btn-close"
              aria-label="Close match details"
              style={{
                position: 'absolute',
                top: '1rem',
                right: '1rem',
                background: 'none',
                border: 'none',
                fontSize: '1.5rem',
                cursor: 'pointer'
              }}
            >
              ×
            </button>
            <h2 id="modal-title" style={{ marginBottom: '1rem' }}>
              {`${modalContent.homeTeam} vs ${modalContent.awayTeam}`}
            </h2>
            <dl style={{ marginBottom: '1rem' }}>
              <dt style={{ fontWeight: '600', marginBottom: '0.5rem' }}>{t('date')}:</dt>
              <dd style={{ marginLeft: '1rem', marginBottom: '1rem' }}>
                {new Date(modalContent.date).toLocaleDateString()}
              </dd>
              <dt style={{ fontWeight: '600', marginBottom: '0.5rem' }}>{t('score')}:</dt>
              <dd style={{ marginLeft: '1rem', marginBottom: '1rem' }}>
                {modalContent.fthg} - {modalContent.ftag}
              </dd>
            </dl>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <button onClick={closeModal} className="btn btn-secondary">
                {t('close')}
              </button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default Championships;
